/**
 * Ukrainian national significant digits (the 9 that fill the
 * `+380 (XX) XXX-XX-XX` input mask), extracted from any stored representation
 * (`+380 (67) 707-79-92`, `0677077992`, `677077992`, …).
 *
 * The mask renders `+380` as a literal prefix, so the value handed to it must
 * NOT include the country code — otherwise the mask re-consumes `380` into its
 * digit slots and shifts the whole number.
 */
export function toNationalUaDigits(phone?: string | null): string {
  if (!phone) return '';
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('380')) {
    digits = digits.slice(3);
  } else if (digits.length > 9 && digits.startsWith('0')) {
    digits = digits.slice(1); // drop the trunk prefix
  }
  return digits.slice(0, 9);
}

const UA_PHONE_MASK = '+380 (__) ___-__-__';

/**
 * Render a stored phone into the masked `+380 (XX) XXX-XX-XX` string.
 *
 * `@react-input/mask`'s `InputMask` only formats characters as the user types;
 * a controlled `value` set from outside is rendered verbatim. So a pre-filled
 * field (profile, station request) must be seeded with the already-masked
 * string, otherwise the raw national digits show up unmasked.
 */
export function toMaskedUaPhone(phone?: string | null): string {
  const digits = toNationalUaDigits(phone);
  if (!digits) return '';

  let result = '';
  let i = 0;
  for (const ch of UA_PHONE_MASK) {
    if (ch !== '_') {
      result += ch;
    } else if (i < digits.length) {
      result += digits[i++];
    } else {
      break; // stop at the first empty slot; the mask placeholder fills the rest
    }
  }
  return result;
}
